import React, { useEffect, useState } from "react";
import { fetchReportReasons, fileReport } from "../lib/api";

/**
 * ReportPlayerModal — file a report against another player.
 *
 * The reasons list is fetched from /api/reports/reasons so the UI stays
 * in sync with the backend whitelist (keeps the radio chooser honest).
 */
export function ReportPlayerModal({ open, player, onClose, onToast }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchReportReasons().then(setReasons).catch(() => setReasons([]));
    setReason("");
    setDetails("");
  }, [open]);

  if (!open || !player) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!reason) { onToast?.("Pick a reason."); return; }
    setBusy(true);
    try {
      await fileReport(player.user_id, reason, details);
      onToast?.(
        `🚩 Report filed against ${player.name || "player"}. Mods will review.`
      );
      onClose?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't file the report.");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="report-player-modal"
      className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-md p-4 flex items-center justify-center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        className="glass-strong rounded-3xl w-full max-w-md p-6 border-2 border-[#14b8a6]/60"
      >
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-flag mr-1" />REPORT PLAYER
        </p>
        <p className="font-display text-xl mt-1">{player.name}</p>
        <p className="text-[11px] text-white/55 mt-1">
          Your report goes to the moderation team. Abuse of the report system
          may result in action against your own account.
        </p>

        <div className="mt-4 space-y-1.5" data-testid="report-reasons">
          {reasons.map((r) => (
            <label
              key={r.code}
              data-testid={`report-reason-${r.code}`}
              className={`flex items-center gap-2 p-2.5 rounded-xl cursor-pointer transition ${
                reason === r.code
                  ? "bg-[#14b8a6]/20 border border-[#14b8a6]/60"
                  : "bg-black/30 border border-white/10 hover:border-[#14b8a6]/30"
              }`}
            >
              <input
                type="radio"
                name="reason"
                value={r.code}
                checked={reason === r.code}
                onChange={() => setReason(r.code)}
                className="accent-[#14b8a6]"
              />
              <span className="text-[12px] text-white/90">{r.label}</span>
            </label>
          ))}
        </div>

        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          maxLength={1500}
          rows={3}
          placeholder="Optional details — what happened, when, links if any."
          data-testid="report-details"
          className="mt-3 w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
        />

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="report-cancel"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost"
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={busy || !reason}
            data-testid="report-submit"
            className="text-[11px] tracking-widest px-5 py-2 rounded-xl bg-[#14b8a6] text-black font-bold disabled:opacity-60"
          >
            {busy ? "FILING…" : "🚩 FILE REPORT"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ReportPlayerModal;
